import type { CompletionSource } from "@codemirror/autocomplete"
import type { Extension } from "@codemirror/state"
import type { JSONSchema7 } from "json-schema"
import { Suspense, lazy, useEffect, useState } from "react"
import { Field, FieldDescriptionOrError, cn, useTheme } from "@motley/design"
import { getExtensions, getTheme } from "./getExtensions"
import type { JsonValidator, ScriptLanguage } from "./model"

const CodeMirror = lazy(() => import("@uiw/react-codemirror"))

type Props = {
  className?: string
  language: ScriptLanguage | "json"
  code: string
  onChange: (code: string) => void
  helperText?: string
  error?: string
  readOnly?: boolean
  fillHeight?: boolean
  jsonSchema?: JSONSchema7
  validate?: JsonValidator
  complete?: CompletionSource
}

export default function CodeEditor({
  className,
  language,
  code,
  onChange,
  helperText,
  error,
  readOnly,
  fillHeight = false,
  jsonSchema,
  validate,
  complete,
}: Props) {
  const { colorScheme } = useTheme()
  const [extensions, setExtensions] = useState<Extension[]>([])
  const [codeTheme, setCodeTheme] = useState<Extension>()

  useEffect(() => {
    void (async () => {
      setExtensions(
        await getExtensions(language, jsonSchema, validate, complete),
      )
    })()
  }, [language, jsonSchema, validate, complete])

  useEffect(() => {
    void (async () => {
      setCodeTheme(await getTheme(colorScheme))
    })()
  }, [colorScheme])

  return (
    <Field
      className={cn(
        "w-full",
        fillHeight ? "min-h-0 flex-1" : "min-h-50",
        className,
      )}
      aria-invalid={!!error}
    >
      <Suspense>
        <CodeMirror
          key={language}
          className={cn(
            "cursor-text overflow-auto border",
            error ? "border-destructive" : "border-muted-foreground",
            fillHeight ? "min-h-0 flex-1" : "max-h-120 min-h-40",
          )}
          height={fillHeight ? "100%" : undefined}
          value={code}
          theme={codeTheme}
          extensions={extensions}
          onChange={onChange}
          readOnly={readOnly}
        />
      </Suspense>
      <FieldDescriptionOrError description={helperText} error={error} />
    </Field>
  )
}
